import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vkontakte/vkui';
import { Icon24Search } from '@vkontakte/icons';
import { getAuthHeaders } from '../utils/authUtils';
import logoImage from '../assets/images/logo_512.png';
import YearPickerModal from './forms/Form2/YearPickerModal';
import '../App.css';
import TreeLoader from './common/TreeLoader';
import { API_BASE } from '../config';
import DecimalInput from './fields/DecimalInput';
import { toDecimalComma, toDecimalPoint } from '../utils/decimalUtils';

// Руководство пользователя лежит рядом с лендингом и отдаётся с того же адреса:
// обновить его — значит заменить один файл на сервере, пересборка не нужна.
const USER_GUIDE_URL = '/app/manual/user-guide.pdf';

const MONTH_COLUMNS = [
  { short: 'Янв', full: 'Январь' },
  { short: 'Фев', full: 'Февраль' },
  { short: 'Мар', full: 'Март' },
  { short: 'Апр', full: 'Апрель' },
  { short: 'Май', full: 'Май' },
  { short: 'Июн', full: 'Июнь' },
  { short: 'Июл', full: 'Июль' },
  { short: 'Авг', full: 'Август' },
  { short: 'Сен', full: 'Сентябрь' },
  { short: 'Окт', full: 'Октябрь' },
  { short: 'Ноя', full: 'Ноябрь' },
  { short: 'Дек', full: 'Декабрь' },
];

const FORM1_TEMPLATE_HEADER_STYLE = {
  cursor: 'default',
  padding: '10px',
  backgroundColor: '#f5f5f5',
  borderBottom: '1px solid #ddd',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const MENU_BTN_STYLE = {
  height: '42px',
  paddingLeft: '20px',
  paddingRight: '20px',
  fontSize: '0.95rem',
  fontWeight: '500',
  borderRadius: '6px',
  border: '1px solid #ddd',
  backgroundColor: '#f8f9fa',
  color: '#495057',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
};

const ACTIVE_MENU_BTN_STYLE = {
  ...MENU_BTN_STYLE,
  fontWeight: '600',
  border: '1px solid #6c757d',
  backgroundColor: '#6c757d',
  color: '#fff',
};

const ACTION_BTN_STYLE = {
  padding: '10px 20px',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  minWidth: '140px',
};

const CLEAR_BTN_STYLE = {
  background: '#6c757d',
  color: 'white',
  border: 'none',
  padding: '5px 10px',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
};

function menuBtnHandlers() {
  return {
    onMouseEnter: (e) => {
      e.currentTarget.style.backgroundColor = '#e8ecef';
      e.currentTarget.style.borderColor = '#adb5bd';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.backgroundColor = '#f8f9fa';
      e.currentTarget.style.borderColor = '#ddd';
    },
  };
}

function activeMenuBtnHandlers() {
  return {
    onMouseEnter: (e) => {
      e.currentTarget.style.backgroundColor = '#5a6268';
      e.currentTarget.style.borderColor = '#5a6268';
    },
    onMouseLeave: (e) => {
      e.currentTarget.style.backgroundColor = '#6c757d';
      e.currentTarget.style.borderColor = '#6c757d';
    },
  };
}

function emptyMatrix(subjectIds) {
  const values = {};
  subjectIds.forEach((id) => {
    values[String(id)] = {};
    for (let m = 1; m <= 12; m += 1) {
      values[String(id)][String(m)] = '';
    }
  });
  return values;
}

function mergeMatrixFromApi(subjectIds, apiValues) {
  const values = emptyMatrix(subjectIds);
  if (!apiValues || typeof apiValues !== 'object') {
    return values;
  }
  Object.entries(apiValues).forEach(([sid, months]) => {
    if (!values[sid]) {
      values[sid] = {};
    }
    if (months && typeof months === 'object') {
      Object.entries(months).forEach(([month, area]) => {
        const n = parseFloat(area);
        values[sid][month] = Number.isFinite(n) && n > 0 ? String(n) : '';
      });
    }
  });
  return values;
}

function matrixToPayload(values) {
  const out = {};
  Object.entries(values).forEach(([sid, months]) => {
    const monthObj = {};
    Object.entries(months || {}).forEach(([month, raw]) => {
      const s = String(raw ?? '').trim().replace(',', '.');
      if (!s) {
        return;
      }
      const n = parseFloat(s);
      if (Number.isFinite(n) && n > 0) {
        monthObj[month] = n;
      }
    });
    if (Object.keys(monthObj).length > 0) {
      out[sid] = monthObj;
    }
  });
  return out;
}

function rowTotal(months) {
  return Object.values(months || {}).reduce((sum, raw) => {
    const s = String(raw ?? '').trim().replace(',', '.');
    if (!s) {
      return sum;
    }
    const n = parseFloat(s);
    return Number.isFinite(n) ? sum + n : sum;
  }, 0);
}

function getPlanTypeLabel(planTypes, code) {
  const found = planTypes.find((t) => t.code === code);
  return found ? found.name : '';
}

function SavePlanButton({ disabled, isSaving, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...ACTION_BTN_STYLE,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
      onMouseOver={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = '#5a6268';
        }
      }}
      onMouseOut={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = '#6c757d';
        }
      }}
    >
      {isSaving ? 'Сохранение…' : 'Сохранить'}
    </button>
  );
}

const PlansPage = ({ user, onLogout, canCreateReports = false }) => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const planComboRef = useRef(null);

  const [planTypes, setPlanTypes] = useState([]);
  const [selectedPlanType, setSelectedPlanType] = useState('');
  const [planComboOpen, setPlanComboOpen] = useState(false);
  const [planFilter, setPlanFilter] = useState('');

  const [year, setYear] = useState(currentYear);
  const [subjects, setSubjects] = useState([]);
  const [values, setValues] = useState({});
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const [isLoadingTypes, setIsLoadingTypes] = useState(true);
  const [isLoadingMatrix, setIsLoadingMatrix] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoadingTypes(true);
      try {
        const res = await fetch(`${API_BASE}/plan-types/`, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        });
        if (!res.ok) {
          throw new Error('Не удалось загрузить типы планов');
        }
        const data = await res.json();
        if (!cancelled) {
          setPlanTypes(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || 'Ошибка загрузки типов планов');
        }
      } finally {
        if (!cancelled) {
          setIsLoadingTypes(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (planComboRef.current && !planComboRef.current.contains(e.target)) {
        setPlanComboOpen(false);
        setPlanFilter('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMatrix = useCallback(async () => {
    if (!selectedPlanType || !year) {
      return;
    }
    setIsLoadingMatrix(true);
    setError(null);
    setSaveMessage(null);
    try {
      const params = new URLSearchParams({
        plan_type: selectedPlanType,
        year: String(year),
      });
      const res = await fetch(`${API_BASE}/plans/matrix/?${params}`, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Не удалось загрузить план');
      }
      const data = await res.json();
      const list = Array.isArray(data.subjects) ? data.subjects : [];
      setSubjects(list);
      const ids = list.map((s) => s.id);
      setValues(mergeMatrixFromApi(ids, data.values));
      setMeta({
        updated_at: data.updated_at,
        updated_by: data.updated_by,
      });
    } catch (e) {
      setError(e.message || 'Ошибка загрузки плана');
      setSubjects([]);
      setValues({});
    } finally {
      setIsLoadingMatrix(false);
    }
  }, [selectedPlanType, year]);

  useEffect(() => {
    if (selectedPlanType) {
      loadMatrix();
    }
  }, [selectedPlanType, year, loadMatrix]);

  const clearPlanSelection = () => {
    setSelectedPlanType('');
    setPlanComboOpen(false);
    setPlanFilter('');
    setSubjects([]);
    setValues({});
    setMeta({ updated_at: null, updated_by: null });
    setError(null);
    setSaveMessage(null);
  };

  const handleCellChange = (subjectId, month, raw) => {
    const sid = String(subjectId);
    const m = String(month);
    setValues((prev) => ({
      ...prev,
      [sid]: {
        ...(prev[sid] || {}),
        [m]: raw,
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedPlanType || !year) {
      return;
    }
    setIsSaving(true);
    setError(null);
    setSaveMessage(null);
    try {
      const res = await fetch(`${API_BASE}/plans/matrix/`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan_type: selectedPlanType,
          year,
          values: matrixToPayload(values),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || 'Ошибка сохранения');
      }
      const data = await res.json();
      const ids = subjects.map((s) => s.id);
      setValues(mergeMatrixFromApi(ids, data.values));
      setMeta({
        updated_at: data.updated_at,
        updated_by: data.updated_by,
      });
      setSaveMessage('План сохранён');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (e) {
      setError(e.message || 'Ошибка сохранения');
    } finally {
      setIsSaving(false);
    }
  };

  const filteredPlanOptions = useMemo(() => {
    const q = planFilter.trim().toLowerCase();
    if (!q) {
      return planTypes;
    }
    return planTypes.filter((t) => t.name.toLowerCase().includes(q));
  }, [planTypes, planFilter]);

  const canSave = selectedPlanType && !isLoadingMatrix && !isSaving && subjects.length > 0;
  const menuHover = menuBtnHandlers();
  const activeMenuHover = activeMenuBtnHandlers();

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div
        className="top-menu mlr-0-8 bb-1 row space-between"
        style={{
          height: '5.5vh',
          minHeight: '55px',
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingLeft: '1.5vw',
          paddingRight: '1.5vw',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center', height: '100%', flex: '0 0 auto' }}>
          <img
            src={logoImage}
            alt="Рослес"
            style={{ height: '38px', width: 'auto', objectFit: 'contain', maxHeight: '100%' }}
          />
          <span
            style={{
              fontSize: '1rem',
              fontWeight: '600',
              color: '#495057',
              letterSpacing: '0.5px',
            }}
          >
            ГЛПМ
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', height: '100%', marginLeft: '40px' }}>
            <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={MENU_BTN_STYLE}
                {...menuHover}
                onClick={() => navigate('/')}
              >
                Журнал карточек ВНН
            </Button>
            <Button
              size="m"
              mode="outline"
              appearance="neutral"
              style={MENU_BTN_STYLE}
              {...menuHover}
              onClick={() => window.open(USER_GUIDE_URL, '_blank')}
            >
              Руководство пользователя
            </Button>
            {canCreateReports && (
              <Button
                size="m"
                mode="outline"
                appearance="neutral"
                style={MENU_BTN_STYLE}
                {...menuHover}
                onClick={() => navigate('/reports')}
              >
                Отчёты
              </Button>
            )}
            <Button
              size="m"
              mode="outline"
              appearance="neutral"
              style={ACTIVE_MENU_BTN_STYLE}
              {...activeMenuHover}
              onClick={() => navigate('/plans')}
            >
              Планы
            </Button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', height: '100%', flex: '0 0 auto' }}>
          {user && (
            <>
              <div
                style={{
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  borderLeft: '1px solid #e0e0e0',
                  marginLeft: '8px',
                }}
              >
                <span
                  className="sz-0-8 b-5"
                  style={{ color: '#495057', margin: 0, padding: 0, lineHeight: '1.2', whiteSpace: 'nowrap' }}
                >
                  {user.username}
                </span>
              </div>
              {onLogout && (
                <Button
                  size="m"
                  mode="outline"
                  appearance="neutral"
                  style={MENU_BTN_STYLE}
                  {...menuHover}
                  onClick={onLogout}
                >
                  Выход
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px', paddingTop: '40px' }}>
        <p className="sz-1-3 mt0 b-7" style={{ margin: 0, padding: 0, marginBottom: '48px' }}>
          Планы работ
        </p>

        <div className="template-section">
          <div className="template-content">
            <div style={{ marginBottom: '28px', marginTop: '-10px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <label
                  htmlFor="plan-type-combo-input"
                  className="sz-0-9 b-5"
                  style={{ margin: 0, padding: 0, color: '#495057' }}
                >
                  Какой план открыть <span className="required">*</span>
                </label>
                <button type="button" onClick={clearPlanSelection} style={CLEAR_BTN_STYLE}>
                  Очистить
                </button>
              </div>
              <div ref={planComboRef} style={{ position: 'relative', width: '100%', maxWidth: '100%' }}>
                <div style={{ position: 'relative', width: '100%' }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      color: '#6c757d',
                      pointerEvents: 'none',
                      zIndex: 1,
                    }}
                    aria-hidden
                  >
                    <Icon24Search width={20} height={20} />
                  </span>
                  <input
                    id="plan-type-combo-input"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    role="combobox"
                    aria-expanded={planComboOpen}
                    aria-controls="plan-type-listbox"
                    aria-autocomplete="list"
                    disabled={isLoadingTypes}
                    placeholder={
                      isLoadingTypes ? 'Загрузка типов планов…' : 'Поиск по названию плана…'
                    }
                    value={
                      planComboOpen ? planFilter : getPlanTypeLabel(planTypes, selectedPlanType)
                    }
                    onChange={(e) => {
                      setPlanFilter(e.target.value);
                      setPlanComboOpen(true);
                    }}
                    onFocus={() => {
                      if (!planComboOpen) {
                        setPlanFilter('');
                      }
                      setPlanComboOpen(true);
                    }}
                    className={`form-control ${!selectedPlanType ? 'required-field' : ''}`}
                    style={{
                      width: '100%',
                      minHeight: '44px',
                      padding: '10px 14px 10px 44px',
                      fontSize: '0.95rem',
                      fontWeight: '500',
                      color: '#495057',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      boxSizing: 'border-box',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                    }}
                  />
                </div>
                {planComboOpen && !isLoadingTypes && (
                  <ul
                    id="plan-type-listbox"
                    role="listbox"
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: '100%',
                      marginTop: '6px',
                      marginBottom: 0,
                      padding: '6px 0',
                      maxHeight: 'min(50vh, 300px)',
                      overflowY: 'auto',
                      listStyle: 'none',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      zIndex: 200,
                    }}
                  >
                    {filteredPlanOptions.length === 0 ? (
                      <li style={{ padding: '12px 16px', color: '#6c757d', fontSize: '0.9rem' }}>
                        {planTypes.length === 0 ? 'Нет доступных типов планов' : 'Ничего не найдено'}
                      </li>
                    ) : (
                      filteredPlanOptions.map((opt) => (
                        <li
                          key={opt.code}
                          role="option"
                          aria-selected={selectedPlanType === opt.code}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setSelectedPlanType(opt.code);
                            setPlanComboOpen(false);
                            setPlanFilter('');
                            setSaveMessage(null);
                          }}
                          style={{
                            padding: '11px 16px',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            color: '#495057',
                            cursor: 'pointer',
                            lineHeight: 1.35,
                            backgroundColor:
                              selectedPlanType === opt.code ? '#e8ecef' : 'transparent',
                          }}
                          onMouseEnter={(e) => {
                            if (selectedPlanType !== opt.code) {
                              e.currentTarget.style.backgroundColor = '#f1f3f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor =
                              selectedPlanType === opt.code ? '#e8ecef' : 'transparent';
                          }}
                        >
                          {opt.name}
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 14px',
              backgroundColor: '#f8d7da',
              color: '#842029',
              borderRadius: '6px',
              border: '1px solid #f5c2c7',
              fontSize: '0.9rem',
            }}
          >
            {error}
          </div>
        )}
        {saveMessage && (
          <div
            style={{
              marginBottom: '16px',
              padding: '10px 14px',
              backgroundColor: '#d1e7dd',
              color: '#0f5132',
              borderRadius: '6px',
              border: '1px solid #badbcc',
              fontSize: '0.9rem',
            }}
          >
            {saveMessage}
          </div>
        )}

        {selectedPlanType && (
          <>
            <div className="template-header" style={FORM1_TEMPLATE_HEADER_STYLE}>
              <h3>II. Плановые объёмы по субъектам РФ</h3>
            </div>
            <div className="template-content">
              <div className="group-section">
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}
                >
                  <h4 style={{ margin: 0 }}>Параметры плана</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <label
                      className="sz-0-9 b-5"
                      style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, color: '#495057' }}
                    >
                      Год
                      <div
                        className="reference-cell"
                        onClick={() => setShowYearPicker(true)}
                        title="Выбрать год"
                        style={{
                          minHeight: 36,
                          height: 36,
                          width: 88,
                          cursor: 'pointer',
                          background: 'transparent',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <textarea
                          readOnly
                          tabIndex={-1}
                          value={year ? String(year) : ''}
                          placeholder="☰"
                          className="form-control coordinate-field"
                          style={{
                            border: 'none',
                            outline: 'none',
                            resize: 'none',
                            width: '100%',
                            height: '100%',
                            background: 'transparent',
                            color: year ? '#495057' : '#6c757d',
                            fontStyle: year ? 'normal' : 'italic',
                            textAlign: 'center',
                            fontSize: '0.9rem',
                            padding: '6px 0',
                            boxSizing: 'border-box',
                            cursor: 'pointer',
                          }}
                        />
                      </div>
                    </label>
                    <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                      Единица: <strong>га</strong>
                      {meta.updated_at && (
                        <>
                          {' '}
                          · Обновлено: {new Date(meta.updated_at).toLocaleString('ru-RU')}
                        </>
                      )}
                    </span>
                    <SavePlanButton
                      disabled={!canSave}
                      isSaving={isSaving}
                      onClick={handleSave}
                    />
                  </div>
                </div>

                {isLoadingMatrix ? (
                  <TreeLoader label="Загрузка планов…" />
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table plans-matrix-table">
                      <thead>
                        <tr>
                          <th className="plans-col-subject">Субъект РФ</th>
                          {MONTH_COLUMNS.map((col) => (
                            <th key={col.short} className="plans-col-month" title={col.full}>
                              {col.short}
                              <div className="plans-col-unit">га</div>
                            </th>
                          ))}
                          <th className="plans-col-total">Итого</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjects.map((subject) => {
                          const sid = String(subject.id);
                          const months = values[sid] || {};
                          const total = rowTotal(months);
                          return (
                            <tr key={subject.id}>
                              <td className="plans-col-subject plans-subject-name">
                                {subject.name}
                              </td>
                              {MONTH_COLUMNS.map((_, idx) => {
                                const m = String(idx + 1);
                                return (
                                  <td key={m} className="coordinate-cell plans-col-month">
                                    <DecimalInput
                                      value={months[m] ?? ''}
                                      onChange={(e) =>
                                        handleCellChange(subject.id, m, toDecimalPoint(e.target.value))
                                      }
                                      className="form-control coordinate-field plans-month-input"
                                    />
                                  </td>
                                );
                              })}
                              <td className="plans-col-total plans-total-cell">
                                {total > 0 ? toDecimalComma(total.toFixed(2)) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    {!subjects.length && (
                      <p style={{ padding: '24px', color: '#6c757d', textAlign: 'center', margin: 0 }}>
                        Нет субъектов в справочнике
                      </p>
                    )}
                  </div>
                )}

                <div style={{ marginTop: '18px' }}>
                  <SavePlanButton
                    disabled={!canSave}
                    isSaving={isSaving}
                    onClick={handleSave}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {!selectedPlanType && !isLoadingTypes && (
          <p style={{ marginTop: '8px', color: '#6c757d', fontSize: '0.9rem', marginLeft: '4px' }}>
            Выберите тип плана из списка — после этого загрузится таблица по субъектам РФ.
          </p>
        )}
      </div>

      <YearPickerModal
        showYearPicker={showYearPicker}
        displayData={{ glu: String(year) }}
        selectYear={(y) => {
          setYear(y);
          setShowYearPicker(false);
        }}
        closeYearPicker={() => setShowYearPicker(false)}
      />
    </div>
  );
};

export default PlansPage;
